import type { IconType } from 'react-icons'
import type { ProductTier } from '../types/config'
import {
  FiBarChart2,
  FiBookOpen,
  FiBriefcase,
  FiCalendar,
  FiClock,
  FiFileText,
  FiFlag,
  FiList,
  FiMail,
  FiMap,
  FiMessageSquare,
  FiSearch,
  FiShield,
  FiTag,
  FiTrendingUp,
  FiUserPlus,
  FiUsers,
} from 'react-icons/fi'

export type ModuleId =
  | 'canvassing'
  | 'union-management'
  | 'analytics'
  | 'lobby-day'
  | 'communications'
  | 'reporting'

export interface ModuleNavEntry {
  label: string
  icon: IconType
  to: string
  /** pathname prefixes that mark this entry active */
  activePrefixes: string[]
}

export interface ModuleDef {
  id: ModuleId
  label: string
  icon: IconType
  description: string
  status: 'active' | 'coming-soon'
  /** 'outer' modules (union-management, lobby-day) are hidden for the Campaign product
   *  tier; 'inner' modules are visible under every tier. See visibleModules(). */
  tier: 'inner' | 'outer'
  /** where clicking the module (sidebar header or hub tile) navigates */
  basePath: string
  /** any pathname equal to, or nested under, one of these belongs to this module */
  pathPrefixes: string[]
  navItems: ModuleNavEntry[]
  /** shown on the coming-soon page and hub tile hover */
  plannedFeatures?: string[]
}

export const MODULES: ModuleDef[] = [
  {
    id: 'canvassing',
    label: 'Canvassing',
    icon: FiFlag,
    description: 'Campaigns, turf, scripts, and the door program',
    status: 'active',
    tier: 'inner',
    basePath: '/dashboard/campaigns',
    pathPrefixes: [
      '/dashboard/campaigns',
      '/dashboard/canvassing',
      '/dashboard/turfs',
      '/dashboard/turf-maps',
    ],
    navItems: [
      {
        label: 'Campaigns',
        icon: FiFlag,
        to: '/dashboard/campaigns',
        activePrefixes: ['/dashboard/campaigns', '/dashboard/turfs', '/dashboard/turf-maps'],
      },
      {
        label: 'Scripts',
        icon: FiBookOpen,
        to: '/dashboard/canvassing/scripts',
        activePrefixes: ['/dashboard/canvassing/scripts'],
      },
      {
        label: 'Surveys',
        icon: FiFileText,
        to: '/dashboard/canvassing/surveys',
        activePrefixes: ['/dashboard/canvassing/surveys'],
      },
      {
        label: 'Tags',
        icon: FiTag,
        to: '/dashboard/canvassing/tags',
        activePrefixes: ['/dashboard/canvassing/tags'],
      },
    ],
  },
  {
    id: 'union-management',
    label: 'Union Management',
    icon: FiShield,
    description: 'Businesses, worksites, unions, stewards, and employment',
    status: 'active',
    tier: 'outer',
    basePath: '/dashboard/unions',
    pathPrefixes: ['/dashboard/worksites', '/dashboard/businesses', '/dashboard/unions'],
    navItems: [
      {
        label: 'Businesses & Worksites',
        icon: FiBriefcase,
        to: '/dashboard/worksites',
        activePrefixes: ['/dashboard/worksites', '/dashboard/businesses'],
      },
      {
        label: 'Unions',
        icon: FiShield,
        to: '/dashboard/unions',
        activePrefixes: ['/dashboard/unions'],
      },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics & Elections',
    icon: FiBarChart2,
    description: 'Election analytics, partisan lean, and the analytics map',
    status: 'active',
    tier: 'inner',
    basePath: '/dashboard/analytics-map',
    pathPrefixes: ['/dashboard/analytics-map', '/dashboard/analytics'],
    navItems: [
      {
        label: 'Analytics Map',
        icon: FiMap,
        to: '/dashboard/analytics-map',
        activePrefixes: ['/dashboard/analytics-map'],
      },
      {
        label: 'Scenarios',
        icon: FiTrendingUp,
        to: '/dashboard/analytics/scenarios',
        activePrefixes: ['/dashboard/analytics/scenarios'],
      },
    ],
  },
  {
    id: 'lobby-day',
    label: 'Lobby Day',
    icon: FiCalendar,
    description: 'Drag-and-drop lobby day scheduling with constituency checks',
    status: 'active',
    tier: 'outer',
    basePath: '/dashboard/lobby-day',
    pathPrefixes: ['/dashboard/lobby-day'],
    navItems: [
      {
        label: 'Events',
        icon: FiCalendar,
        to: '/dashboard/lobby-day',
        activePrefixes: ['/dashboard/lobby-day'],
      },
      {
        label: 'Legislators',
        icon: FiUsers,
        to: '/dashboard/lobby-day/legislators',
        activePrefixes: ['/dashboard/lobby-day/legislators'],
      },
    ],
  },
  {
    id: 'reporting',
    label: 'Reporting',
    icon: FiFileText,
    description: 'Campaign post-mortems, lobby day outcomes, and activity summaries',
    status: 'active',
    tier: 'inner',
    basePath: '/dashboard/reports',
    pathPrefixes: ['/dashboard/reports'],
    navItems: [
      { label: 'Reports', icon: FiFileText, to: '/dashboard/reports', activePrefixes: ['/dashboard/reports'] },
      { label: 'Saved runs', icon: FiClock, to: '/dashboard/reports/runs', activePrefixes: ['/dashboard/reports/runs'] },
    ],
  },
  {
    id: 'communications',
    label: 'Communications',
    icon: FiMessageSquare,
    description: 'Outbound email, with texting and phone banking still to come',
    status: 'active',
    tier: 'inner',
    // Unlike the other active modules, the module's own bare path (/dashboard/communications)
    // isn't a real page (it's the pre-L4 coming-soon stub, still reachable directly) -- the
    // header click target goes straight to the one landing page that exists, same as
    // "canvassing" landing on /campaigns rather than an index route.
    basePath: '/dashboard/communications/email',
    pathPrefixes: ['/dashboard/communications'],
    navItems: [
      {
        label: 'Email',
        icon: FiMail,
        to: '/dashboard/communications/email',
        activePrefixes: ['/dashboard/communications/email'],
      },
      {
        label: 'Templates',
        icon: FiFileText,
        to: '/dashboard/communications/templates',
        activePrefixes: ['/dashboard/communications/templates'],
      },
    ],
    plannedFeatures: [
      'Not-home auto-text with organizer-controlled templates',
      'Virtual phone numbers (no personal-phone canvassing)',
      'Virtual phone bank campaigns',
    ],
  },
]

/** Always-visible shared-core nav entries (contacts and lists are the data layer, not a module). */
export const CORE_NAV: ModuleNavEntry[] = [
  {
    label: 'Contact Search',
    icon: FiSearch,
    to: '/dashboard/contacts/search',
    activePrefixes: ['/dashboard/contacts/search'],
  },
  {
    label: 'Create lists',
    icon: FiList,
    to: '/dashboard/lists',
    activePrefixes: ['/dashboard/lists', '/dashboard/searches'],
  },
  {
    label: 'Add contact',
    icon: FiUserPlus,
    to: '/dashboard/contacts/new',
    activePrefixes: ['/dashboard/contacts/new'],
  },
]

export function isPrefixActive(pathname: string, prefixes: string[]): boolean {
  return prefixes.some((p) => pathname === p || pathname.startsWith(p + '/'))
}

export function resolveActiveModule(pathname: string): ModuleDef | null {
  return MODULES.find((m) => isPrefixActive(pathname, m.pathPrefixes)) ?? null
}

/** The modules a caller should be shown/rendered, gated by product tier. 'outer' modules
 *  (union-management, lobby-day) are hidden for the Campaign tier; Union sees everything.
 *  Route resolution (resolveActiveModule) intentionally stays ungated -- this only filters
 *  what's rendered in nav/tile/sheet lists. */
export function visibleModules(tier: ProductTier): ModuleDef[] {
  return MODULES.filter((m) => tier === 'Union' || m.tier === 'inner')
}

/** Route → active module, but only when that module is visible for the given tier (SC-15).
 *  An 'outer' module deep-linked under the Campaign tier resolves to null so the mobile
 *  chrome (ContextualTabBar / MobileTopBar) renders the generic top-level shell instead of a
 *  module's nav/title the backend will 404. Enforcement stays server-side; this only keeps the
 *  FE shell from advertising a module the tier can't reach. */
export function resolveVisibleModule(pathname: string, tier: ProductTier): ModuleDef | null {
  const mod = resolveActiveModule(pathname)
  if (!mod) return null
  return mod.tier === 'inner' || tier === 'Union' ? mod : null
}
